import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DocumentViewer } from "../components/DocumentViewer";
import { AssetBody, AssetFailure, type AssetView } from "../components/register/AssetView";
import { TableRowsForTag, type RowCitation } from "../components/register/TableRows";
import { TagGraphSection } from "../components/neio/TagGraph";
import { Loading } from "../components/States";
import { docs, FIELD_LABELS, type Field } from "../lib/api";
import { apiGet } from "../lib/http";

/** /register/:package/:tag. The register's view of one tag, what every module reads. */
export function AssetPage() {
  const { package: pkg = "", tag = "" } = useParams();
  const [cited, setCited] = useState<Field | null>(null);
  const [rowCited, setRowCited] = useState<RowCitation | null>(null);
  // Rows read from the package's tables for this tag. A failure here hides the section only.
  const rows = useQuery({ queryKey: ["table-rows", pkg, tag], queryFn: () => docs.tableRows(pkg, tag), retry: false });
  const query = useQuery({
    queryKey: ["asset", pkg, tag],
    queryFn: async () => (await apiGet("/api/v1/assets/{package}/{tag}", { path: { package: pkg, tag } })) as unknown as AssetView,
  });

  return (
    <div className="page">
      <Link to="/saved" className="row muted" style={{ fontSize: 13, marginBottom: 8 }}>
        <ArrowLeft size={14} aria-hidden="true" /> Saved
      </Link>
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>Tag <span className="mono">{tag}</span></h2>
      <p className="muted" style={{ marginBottom: 20 }}>{query.data?.package ?? pkg} package. Register entry.</p>

      {query.isPending && <Loading what="Reading the register" />}
      {query.isError && <AssetFailure pkg={pkg} tag={tag} error={query.error} onRetry={() => void query.refetch()} />}
      {query.data && <AssetBody asset={query.data} onCite={setCited} />}
      {rows.data && <TableRowsForTag rows={rows.data} onCite={setRowCited} />}

      {rowCited && (
        <DocumentViewer pkg={query.data?.package ?? pkg} path={rowCited.path} page={rowCited.page} box={rowCited.box} label={rowCited.label}
                        onClose={() => setRowCited(null)} />
      )}
      {query.data && <TagGraphSection pkg={query.data.package} tag={query.data.tag} />}

      {cited?.provenance.source_path && cited.provenance.page_number && (
        <DocumentViewer
          pkg={query.data?.package ?? pkg} path={cited.provenance.source_path} page={cited.provenance.page_number} box={cited.provenance.box}
          label={`${FIELD_LABELS[cited.name] ?? cited.name}: ${cited.value ?? ""}`} onClose={() => setCited(null)}
        />
      )}
    </div>
  );
}
